/**
 * FormNoVersions
 *
 * Empty state rendered by FormPageContent in place of FormUI when the form's
 * `formVersions` collection is empty — the state a form lands in once every
 * version has been archived. Forms are allowed to have zero versions, so this
 * is a normal state, not an error: hence antd `Empty`, not `Result`.
 *
 * The action calls `createFormVersion` without a source version, so the callable
 * builds the version from the form type alone (its initial state and default
 * layout), then navigates to it. It reports in flight through the `isCreating`
 * flag on `FormWorkflowActionContext` rather than a local one: the page keeps
 * `FormControl` — and so `FormVersionSelect`'s own New Version action — mounted
 * beside this empty state, so both buttons are live at once and one create must
 * lock the other (SYD-2467).
 */
import { FC, useContext } from 'react';
import { Button, Empty, Typography, theme } from 'antd';
import { useSnapshot } from 'valtio';

import FlexBox from '../../atoms/FlexBox';
import { formActions, formStore } from '../../../store';
import { MessageContext } from '../../../providers/MessageContext';
import { createFormVersion } from '../../../services/artifactFunctions';
import { useFormWorkflowAction } from '../../../providers/artifacts/FormWorkflowActionContext';

const FormNoVersions: FC = () => {
  const { token } = theme.useToken();
  const { messageApi } = useContext(MessageContext);
  const { isCreating, setIsCreating } = useFormWorkflowAction();

  const formId = useSnapshot(formStore.state).formId;

  const handleCreateFirstVersion = async () => {
    if (!formId) return;

    setIsCreating(true);
    try {
      const newVersionId = await createFormVersion(formId, undefined, 'draft');
      formActions.navigateToVersion(formId, newVersionId);
    } catch (err) {
      messageApi.error(
        `Error creating first version: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <FlexBox
      aria-label="form-no-versions"
      justifyCenter
      stretch
      padding={token.paddingLG}
    >
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <FlexBox column justifyCenter gap={0}>
            <Typography.Text
              strong
              style={{ fontSize: token.fontSizeHeading5 }}
            >
              No versions yet
            </Typography.Text>
            <Typography.Text type="secondary">
              Create a version to start working.
            </Typography.Text>
          </FlexBox>
        }
      >
        <Button
          type="primary"
          aria-label="Create first version"
          loading={isCreating}
          disabled={!formId || isCreating}
          onClick={handleCreateFirstVersion}
        >
          Create first version
        </Button>
      </Empty>
    </FlexBox>
  );
};

export default FormNoVersions;
